// src/tabs/StockEcran.jsx — L'ÉCRAN STOCK (maquette validée Dimitri
// 2026-08-21) — le dernier morceau de l'espace orchestrateur.
//
//   · LE BUT N'EST PAS L'INVENTAIRE, C'EST L'ALERTE : quel logement va
//     manquer, quand le central se vide.
//   · BANDEAU 2 blocs cliquables (naviguent vers leur vue) : logements à
//     réapprovisionner (terracotta) · produits bientôt finis au central
//     (ambre). Maquette v677 : ni « chez les agents », ni vue « Les agents » :
//     l'agent de ménage n'a aucun stock (décision 53).
//   · BASCULE 2 vues : « Les logements » (défaut) · « Le central » —
//     + « Les coûts » chez l'ADMIN SEUL.
//   · DEUX SOURCES, JAMAIS MÉLANGÉES : l'agent SIGNALE (un FAIT — carte
//     terracotta, icône main) ; le RYTHME PRÉDIT (une PROBABILITÉ — carte
//     ambre, icône courbe). La couleur et l'icône disent lequel c'est.
//   · LA PROCHAINE ARRIVÉE sur chaque carte logement (heure du CONTRAT).
//   · L'ORCHESTRATEUR NE VOIT AUCUN PRIX — les coûts par ménage vivent chez
//     l'admin seul (« 6,80 € par ménage » : c'est LUI qui dit si un logement
//     consomme anormalement).
//
// UN appel serveur (stock_vue) + resynchro sur fbz:stock-signalements.

import { useState, useEffect, useMemo } from 'react'
import { DS } from '../lib/ui'
import CarteTitrePage from '../components/CarteTitrePage'
import BasculeEspace from '../components/BasculeEspace'
import { fetchStockVue, reglerManque } from '../lib/stock-signalements-api'
import { nomProduit } from '../lib/consommables-helpers'
import { produitsBientotFinis, predictionsLogement } from '../lib/stock-rythme'
import { getAllLogsFlat } from '../lib/sousloc-helpers'
import {
  Home, Boxes, Hand, TrendingDown, CircleAlert, PackagePlus, Euro, Check,
} from 'lucide-react'

const TERRA = '#b8412b'
const TERRA_SOMBRE = '#8f2f1d'
const AMBRE = '#d4a24c'
const AMBRE_SOMBRE = '#8a5f19'
const VERT_SOMBRE = '#1a4f3a'
const CIEL = '#5c8ab8'

function aujourdHuiParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
}
function dateCourteFR(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}
function heureFR(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''))
  if (!m) return null
  return m[2] === '00' ? `${Number(m[1])}h` : `${Number(m[1])}h${m[2]}`
}

export default function StockEcran({ cfg, currentUser, isAdmin, fmtE, depotsMois, runsMoisParLogement, onDeposer }) {
  const [vue, setVue] = useState('logements') // JAMAIS mémorisé — défaut logements.
  const [donnees, setDonnees] = useState(null)
  const [chargement, setChargement] = useState(true)
  const [version, setVersion] = useState(0)
  const [erreurRegler, setErreurRegler] = useState(null) // { logId, message } | null

  useEffect(() => {
    const maj = () => setVersion((v) => v + 1)
    window.addEventListener('fbz:stock-signalements', maj)
    return () => window.removeEventListener('fbz:stock-signalements', maj)
  }, [])
  useEffect(() => {
    let annule = false
    fetchStockVue().then((d) => {
      if (annule) return
      setDonnees(d)
      setChargement(false)
    })
    return () => { annule = true }
  }, [version])

  const auj = aujourdHuiParis()
  const logements = useMemo(() => {
    const ids = isAdmin
      ? getAllLogsFlat(cfg).map((l) => l.id)
      : (Array.isArray(currentUser?.logIds) ? currentUser.logIds : [])
    const flat = getAllLogsFlat(cfg)
    // UN CODE NE S'AFFICHE JAMAIS (vu en production le 30/10 : « l8 ») : un
    // identifiant sans fiche (une maison à chambres) n'est pas un logement ici.
    return ids
      .map((id) => ({ id, nom: (flat.find((l) => l.id === id) || {}).nom || null }))
      .filter((l) => l.nom)
  }, [cfg, currentUser, isAdmin])

  const signalements = donnees?.signalements || []
  const depots = donnees?.depots || []
  const central = donnees?.central || []
  const arrivees = donnees?.arrivees || {}

  // LES DEUX SOURCES, calculées par logement — jamais fusionnées.
  const parLogement = useMemo(() => logements.map((l) => {
    const duLogement = depots.filter((d) => d?.logId === l.id)
    const parProduit = {}
    for (const d of duLogement) for (const li of (d.lignes || [])) {
      if (li?.csId) parProduit[li.csId] = (parProduit[li.csId] || 0) + 1
    }
    return {
      ...l,
      faits: signalements.filter((s) => s.logement_id === l.id),
      predictions: predictionsLogement(depots, l.id, auj),
      arrivee: arrivees[l.id] || null,
      assezHistorique: Object.values(parProduit).some((n) => n >= 3),
    }
  }), [logements, signalements, depots, arrivees, auj])

  const aReapprovisionner = parLogement.filter((l) => l.faits.length || l.predictions.length)
  const bientotFinis = useMemo(
    () => produitsBientotFinis(central.map((c) => ({ csId: c.cs_id, qtyDispo: c.qty })), depots, auj),
    [central, depots, auj])
  // ⚠️ LE SOUS-SEUIL AUSSI (décision Dimitri 2026-08-22) : le badge de la
  // barre compte ce qui DEMANDE UNE ACTION — sous seuil ET bientôt fini. La
  // page ne montrait que le rythme (« bientôt finis », muet sans 3 dépôts) et
  // annonçait « 0 produit » quand deux références étaient sous leur seuil.
  // Les deux vivent, séparément : ils ne disent pas la même chose.
  const sousSeuil = useMemo(
    () => central.filter((c) => c.seuil > 0 && c.qty <= c.seuil),
    [central])
  const bientotSansSousSeuil = useMemo(() => {
    const dejaSous = new Set(sousSeuil.map((c) => c.cs_id))
    return bientotFinis.filter((b) => !dejaSous.has(b.csId || b.cs_id))
  }, [bientotFinis, sousSeuil])

  const nomP = (csId) => nomProduit(cfg, csId)

  // ── LE BANDEAU — deux blocs qui NAVIGUENT. ──
  const blocs = [
    { cle: 'logements', fond: TERRA, Ico: Home,
      compte: `${aReapprovisionner.length} logement${aReapprovisionner.length > 1 ? 's' : ''}`,
      precision: 'à réapprovisionner', couleur: '#f5c4b4', teinte: 'rgba(184,65,43,.25)' },
    { cle: 'central', fond: AMBRE, Ico: TrendingDown,
      compte: `${sousSeuil.length + bientotSansSousSeuil.length} produit${(sousSeuil.length + bientotSansSousSeuil.length) > 1 ? 's' : ''}`,
      // LE MÊME COMPTE QUE LE BADGE — et la précision DIT ce qu'il additionne.
      precision: sousSeuil.length && bientotSansSousSeuil.length
        ? `${sousSeuil.length} sous seuil · ${bientotSansSousSeuil.length} bientôt fini${bientotSansSousSeuil.length > 1 ? 's' : ''}`
        : (sousSeuil.length ? 'sous leur seuil au central' : 'bientôt finis au central'),
      couleur: '#f5d9a8' },
  ]

  const carteBlanche = { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 13px' }

  // ── VUE LOGEMENTS ─────────────────────────────────────────────────────────
  const vueLogements = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {parLogement.map((l) => (
        <div key={l.id} data-testid={`stock-logement-${l.id}`} style={carteBlanche}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#233240' }}>{l.nom}</span>
            {l.arrivee && (
              <span data-testid={`stock-arrivee-${l.id}`}
                style={{ fontSize: 11, color: CIEL, fontWeight: 600 }}>
                arrivée {l.arrivee.date === auj ? '' : `${dateCourteFR(l.arrivee.date)} `}{heureFR(l.arrivee.heure) || ''}
              </span>
            )}
          </div>

          {/* LE FAIT — terracotta, icône MAIN : l'agent l'a dit.
              S-b (tri Dimitri 2026-08-21) : les DEUX ÉCHÉANCES ne partagent
              JAMAIS un bloc — arrivée en terracotta, prochain ménage en ambre. */}
          {l.faits.filter((s) => s.echeance === 'aujourdhui').length > 0 && (
            <div data-testid={`stock-fait-${l.id}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8,
                background: '#fdf4f0', border: '1px solid #e0b5a5', borderRadius: 9,
                padding: '9px 11px' }}>
              <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 7,
                background: TERRA, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0 }}>
                <Hand size={13} strokeWidth={2.2} color="#fff" />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12, color: TERRA_SOMBRE }}>
                  Il manque pour cette arrivée : <strong>{l.faits.filter((s) => s.echeance === 'aujourdhui').map((s) => (s.cs_id ? nomP(s.cs_id) : s.texte)).join(', ')}</strong>
                </span>
                <span style={{ display: 'block', fontSize: 10, color: '#98a2ad', marginTop: 1 }}>
                  signalé par {[...new Set(l.faits.filter((s) => s.echeance === 'aujourdhui').map((s) => s.par_prenom))].join(', ')}
                </span>
              </span>
              <button type="button" data-testid={`stock-fait-regler-${l.id}`}
                aria-label="Marquer réglé" title="Marquer réglé"
                onClick={() => {
                  // Lot « travail perdu » : ce bouton est DANS le bloc « cette
                  // arrivée » → il ne clôt QUE l'échéance « aujourdhui ». Avant,
                  // il réglait aussi le « prochain ménage » (bloc ambre), qui
                  // disparaissait sans avoir été traité. Échec = message affiché.
                  setErreurRegler(null)
                  Promise.all(l.faits.filter((s) => s.echeance === 'aujourdhui').map((s) => reglerManque(s.id)))
                    .catch((e) => setErreurRegler({ logId: l.id, message: e?.message || String(e) }))
                }}
                className="fbz-press"
                style={{ marginLeft: 'auto', flexShrink: 0, width: 26, height: 26,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 7, border: '1px solid #c9e0d4', background: '#fff',
                  cursor: 'pointer', color: VERT_SOMBRE }}>
                <Check size={13} strokeWidth={2.4} />
              </button>
            </div>
          )}
          {erreurRegler && erreurRegler.logId === l.id && (
            <div data-testid={`stock-fait-erreur-${l.id}`}
              style={{ marginTop: 6, fontSize: 12, color: DS.dangerText }}>
              Non réglé : {erreurRegler.message}
            </div>
          )}

          {/* LE FAIT « PROCHAIN MÉNAGE » — ambre, même main : un fait aussi,
              mais l'autre échéance. */}
          {l.faits.filter((s) => s.echeance === 'prochaine_fois').length > 0 && (
            <div data-testid={`stock-fait-prochain-${l.id}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8,
                background: '#fdf7ee', border: '1px solid #d4a24c', borderRadius: 9,
                padding: '9px 11px' }}>
              <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 7,
                background: AMBRE, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0 }}>
                <Hand size={13} strokeWidth={2.2} color="#fff" />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12, color: AMBRE_SOMBRE }}>
                  Il manquera pour le prochain ménage : <strong>{l.faits.filter((s) => s.echeance === 'prochaine_fois').map((s) => (s.cs_id ? nomP(s.cs_id) : s.texte)).join(', ')}</strong>
                </span>
                <span style={{ display: 'block', fontSize: 10, color: '#98a2ad', marginTop: 1 }}>
                  signalé par {[...new Set(l.faits.filter((s) => s.echeance === 'prochaine_fois').map((s) => s.par_prenom))].join(', ')}
                </span>
              </span>
            </div>
          )}

          {/* LA PRÉDICTION — ambre, icône COURBE : un calcul, pas un fait. */}
          {l.predictions.length > 0 && (
            <div data-testid={`stock-prediction-${l.id}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8,
                background: '#fdf7ee', border: '1px solid #e8d4b0', borderRadius: 9,
                padding: '9px 11px' }}>
              <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 7,
                background: CIEL, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0 }}>
                <TrendingDown size={13} strokeWidth={2.2} color="#fff" />
              </span>
              <span style={{ minWidth: 0, fontSize: 12, color: AMBRE_SOMBRE }}>
                Au rythme habituel, prévoir : <strong>{l.predictions.map((p) => nomP(p.csId)).join(', ')}</strong>
                <span style={{ display: 'block', fontSize: 10, color: '#98a2ad', marginTop: 1 }}>
                  dernier dépôt il y a {l.predictions[0].joursDepuis} j — d'habitude tous les {l.predictions[0].intervalleJours} j
                </span>
              </span>
            </div>
          )}

          {(l.faits.length > 0 || l.predictions.length > 0) && onDeposer && (
            <button type="button" data-testid={`stock-deposer-${l.id}`}
              onClick={() => onDeposer(l.id)} className="fbz-press"
              style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                minHeight: 36, padding: '0 13px', background: '#fff',
                border: `1px solid ${CIEL}`, borderRadius: 9, color: CIEL,
                fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
              <PackagePlus size={14} strokeWidth={2.2} /> Déposer
            </button>
          )}

          {/* critique 2 (actée) : le silence ne RASSURE JAMAIS À TORT — sans
              assez d'historique, on le dit. */}
          {l.faits.length === 0 && l.predictions.length === 0 && (
            <div data-testid={`stock-calme-${l.id}`}
              style={{ fontSize: 11, color: DS.textMuted, marginTop: 6 }}>
              {l.assezHistorique
                ? 'rien à signaler, rien à prévoir'
                : "rien à signaler — pas encore assez d'historique pour prédire"}
            </div>
          )}
        </div>
      ))}
    </div>
  )

  // ── VUE CENTRAL — quantités + prédiction, 0 prix. ─────────────────────────
  const vueCentral = (
    <div style={carteBlanche} data-testid="stock-central">
      {central.length === 0 && (
        <div style={{ fontSize: 12.5, color: DS.textMuted, textAlign: 'center', padding: 10 }}>
          Le stock central est vide — les achats le remplissent.
        </div>
      )}
      {central.map((c, i) => {
        const pred = bientotFinis.find((p) => p.csId === c.cs_id)
        const sous = c.seuil > 0 && c.qty <= c.seuil
        return (
          <div key={c.cs_id} data-testid={`stock-central-${c.cs_id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40,
              borderBottom: i < central.length - 1 ? '1px solid #f2f4f6' : 'none' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: '#233240',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nomP(c.cs_id)}
            </span>
            {/* SOUS SEUIL — le motif que le badge compte, DIT sur la ligne
                (décision Dimitri 2026-08-22) : une quantité en terracotta ne
                nommait pas sa raison. Terracotta = « demande une action »,
                la même couleur que partout ailleurs. */}
            {sous && (
              <span data-testid={`stock-central-sous-seuil-${c.cs_id}`}
                style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: TERRA_SOMBRE,
                  background: '#fdf4f0', border: '1px solid #e0b5a5', borderRadius: 7,
                  padding: '3px 8px' }}>
                sous seuil
              </span>
            )}
            {pred && (
              <span data-testid={`stock-central-pred-${c.cs_id}`}
                style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: AMBRE_SOMBRE,
                  background: '#fdf7ee', border: '1px solid #e8d4b0', borderRadius: 7,
                  padding: '3px 8px' }}>
                ~{pred.joursRestants} j restants
              </span>
            )}
            <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 700,
              color: sous ? TERRA_SOMBRE : '#233240', fontVariantNumeric: 'tabular-nums' }}>
              {c.qty}
            </span>
            <span style={{ flexShrink: 0, fontSize: 10, color: DS.textMuted }}>
              / seuil {c.seuil || '—'}
            </span>
          </div>
        )
      })}
    </div>
  )

  // ── VUE COÛTS — ADMIN SEUL : le coût PAR MÉNAGE dit l'anormal. ────────────
  const vueCouts = isAdmin ? (
    <div style={carteBlanche} data-testid="stock-couts">
      {logements.map((l, i) => {
        const depotsDuLog = (depotsMois || []).filter((d) => d.logId === l.id)
        const total = Math.round(depotsDuLog.reduce((s, d) => s + (Number(d.coutTotal) || 0), 0) * 100) / 100
        const nbMenages = (runsMoisParLogement || {})[l.id] || 0
        const parMenage = nbMenages > 0 ? Math.round((total / nbMenages) * 100) / 100 : null
        return (
          <div key={l.id} data-testid={`stock-cout-${l.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40,
              borderBottom: i < logements.length - 1 ? '1px solid #f2f4f6' : 'none' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#233240',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.nom}</span>
            <span style={{ flexShrink: 0, fontSize: 11, color: DS.textSecondary }}>
              {depotsDuLog.length} dépôt{depotsDuLog.length > 1 ? 's' : ''}
            </span>
            {parMenage != null && (
              <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: TERRA_SOMBRE,
                fontVariantNumeric: 'tabular-nums' }}>
                {fmtE ? fmtE(parMenage) : `${parMenage} €`} / ménage
              </span>
            )}
            <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#233240',
              fontVariantNumeric: 'tabular-nums' }}>
              {fmtE ? fmtE(total) : `${total} €`}
            </span>
          </div>
        )
      })}
    </div>
  ) : null

  const entrees = [
    { id: 'logements', label: 'Les logements', icone: Home },
    { id: 'central', label: 'Le central', icone: Boxes },
    ...(isAdmin ? [{ id: 'couts', label: 'Les coûts', icone: Euro }] : []),
  ]

  return (
    <div data-testid="stock-ecran">
      <CarteTitrePage
        testId="stock-bandeau"
        titre={(
          <span style={{ fontSize: 20 }}>
            Stock{' '}
            <span style={{ fontSize: 11.5, fontWeight: 400, fontFamily: DS.fontBody,
              color: 'rgba(255,255,255,.75)' }}>
              l'alerte, pas l'inventaire
            </span>
          </span>
        )}
        sousLeFilet={(
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap' }}>
            {blocs.map(({ cle, fond, Ico, compte, precision, couleur }) => (
              <button key={cle} type="button" className="fbz-press"
                data-testid={`stock-bloc-${cle}`}
                onClick={() => setVue(cle)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 44,
                  padding: '6px 12px', borderRadius: 10, border: 'none',
                  background: cle === 'logements' ? 'rgba(184,65,43,.28)' : 'rgba(255,255,255,.13)',
                  cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody }}>
                <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9,
                  background: fond, flexShrink: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Ico size={16} strokeWidth={2.2} color="#fff" />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                    {compte}
                  </span>
                  <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: couleur }}>
                    {precision}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      />

      <BasculeEspace testId="bascule-stock" actif={vue} onChange={setVue} entrees={entrees} />

      {chargement && (
        <div style={{ padding: 24, textAlign: 'center', color: DS.textMuted, fontSize: 13 }}>Chargement…</div>
      )}
      {!chargement && !donnees && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 14,
          background: DS.dangerBg, color: DS.dangerText, borderRadius: 10, fontSize: 13 }}>
          <CircleAlert size={16} strokeWidth={2} /> Lecture du stock impossible — réessayez.
        </div>
      )}
      {!chargement && donnees && (
        vue === 'logements' ? vueLogements
          : vue === 'central' ? vueCentral
            : vueCouts
      )}
    </div>
  )
}
